import { useEffect, useState } from "react";
import { Map as MapIcon } from "lucide-react";
import api from "../../lib/api";
import MapView from "../MapView";

/* Small route map on the trip page — geocoded stops + transport legs. */
export default function TripMap({ trip }) {
  const [data, setData] = useState(null);
  const hasPlaces = (trip.stops || []).length > 0 ||
    (trip.segments || []).some((s) => s.from_place || s.to_place);

  useEffect(() => {
    if (!hasPlaces) return;
    api.get(`/trips/${trip.id}/map`).then((r) => setData(r.data)).catch(() => {});
  }, [trip.id, trip.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return null;
  const sidePoints = data.side_points || [];
  const sideLegs = data.side_legs || [];
  const allPoints = [...data.points, ...sidePoints];
  if (allPoints.length === 0) return null;
  const lats = allPoints.map((p) => p.lat);
  const lons = allPoints.map((p) => p.lon);
  const center = [(Math.min(...lats) + Math.max(...lats)) / 2, (Math.min(...lons) + Math.max(...lons)) / 2];
  const span = Math.max(Math.max(...lats) - Math.min(...lats), Math.max(...lons) - Math.min(...lons));
  const zoom = span > 60 ? 2 : span > 25 ? 3 : span > 10 ? 4 : span > 4 ? 6 : span > 1 ? 8 : 10;

  return (
    <section id="trip-map-section" className="mb-10" data-testid="trip-map">
      <h2 className="font-heading font-900 text-xl flex items-center gap-2 mb-3">
        <MapIcon className="w-5 h-5 text-primary" /> Route map
      </h2>
      <MapView center={center} zoom={zoom} height="320px"
        markers={[
          ...data.points.map((p) => ({ lat: p.lat, lon: p.lon, radius: 6, popup: p.name })),
          ...sidePoints.map((p) => ({ lat: p.lat, lon: p.lon, radius: 5, color: "#7C3AED", popup: `${p.name} — side trip: ${p.trip}` })),
        ]}
        lines={[
          ...data.legs.map((l) => ({ from: l.from, to: l.to, style: { color: "#0047FF", weight: 2, opacity: 0.65 } })),
          ...sideLegs.map((l) => ({ from: l.from, to: l.to, style: { color: "#7C3AED", weight: 2, opacity: 0.7, dashArray: "6 6" } })),
        ]} />
      <div className="flex flex-wrap items-center gap-4 mt-1.5 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-4 h-0.5 bg-[#0047FF]" /> Main route</span>
        {sideLegs.length > 0 && (
          <span className="inline-flex items-center gap-1.5" data-testid="trip-map-side-legend">
            <span className="inline-block w-4 border-t-2 border-dashed border-[#7C3AED]" /> Side trips
          </span>
        )}
        <span>Placed automatically from your itinerary place names.</span>
      </div>
    </section>
  );
}
